import { history } from 'umi';
import { useFormatMsg, locale } from '@/utils/locale';
import { extend, ResponseError, RequestMethod } from 'umi-request';
import { v4 } from 'uuid';
import appInfo from '@/stores/appInfo';
import common from '@/utils/v2/common';
import { Toast as CreditToast } from '@credit/tips';
import insight from '@/utils/v2/insight';
import { ExceptionCode } from '@/types/constant';
import { getEncryptConfig, signature } from '@/utils/v2/encrypt';
import { sendUserAction, UserActions } from './idap';
import { isShopeeSellerApp } from '@/utils/v2/device';

export const NO_PERMISSION = 92019; // 主子账号无权限错误码

const formatMessage = useFormatMsg();

export interface IRequestParams {
  method: 'POST' | 'PUT' | 'DELETE' | 'GET';
  url: string;
  body?: Record<string, any>;
  bodyHeader?: Record<string, any>;
  headers?: Record<string, any>;
  options?: Record<string, any>;
  manual?: boolean;
  isUseDefaultErrorMsg?: boolean;
}

export class Request {
  private umiRequest: RequestMethod;

  private baseHeaders = {
    'X-Kredit-Req-Source': '1',
    'X-Kredit-Req-Language': locale,
    'X-Request-Source': '1', // 1: app, 2: pc
  };

  private version = 1; // 版本， 默认为1
  private platForm = 1; // 平台，1: shopee
  private region: string = BIZ_COUNTRY; // 国家或地区
  private appVersion: number; // app版本号
  private rnVersion: number; // rn版本号
  private shopeeRnBundleVersion: number; // shopee_rn_bundle_version的版本号

  constructor() {
    this.appVersion = common.getAppVersion();
    this.rnVersion = common.getRnVersion();
    this.shopeeRnBundleVersion = common.getShopeeRnBundleVersion();

    this.umiRequest = extend({
      prefix: '/api',
      errorHandler: this.defaultErrorHandler, // 默认错误处理
      credentials: 'include', // 默认请求是否带上cookie
      useCache: false,
      timeout: 10000, // 请求失效的时间
      validateCache: () => false,
      requestType: 'json',
      responseType: 'json',
      getResponse: true,
      parseResponse: true,
      throwErrIfParseFail: true,
    });

    this.umiRequest.interceptors.request.use(
      (url, options) => {
        return {
          url,
          options,
        };
      },
      {
        global: false,
      },
    );

    this.umiRequest.interceptors.response.use(
      async (response, options) => {
        const status = response?.status ?? 0;
        // 处理503维护页逻辑
        if (parseInt(`${status}`, 10) === ExceptionCode.MaintenanceException) {
          let resBody: Record<string, any> = {};
          try {
            resBody = (await response?.clone()?.json()) || {};
          } catch (error) {
            resBody = {};
          }
          // console.log('response.use resBodyresBody', resBody);
          const start = resBody?.result?.start_time || '';
          const end = resBody?.result?.end_time || '';
          const transfer = resBody?.result?.transfer_key || '';
          history.replace(`/maintenance?start=${start}&end=${end}&transfer=${transfer}`);
          const err = { response };
          return Promise.reject(err);
        }
        if (!response || parseInt(`${status}`, 10) >= 400) {
          const err = { response };
          return Promise.reject(err);
        }
        let data: any = await response.clone().json();
        data = this.mixCode(data);
        this.checkSpecCode(data, options);
        return data;
      },
      {
        global: false,
      },
    );
  }

  /**
   * 将UC返回的错误信息提到最外层
   * @param response
   * @returns
   */
  protected mixCode = (response: any) => {
    switch (true) {
      case !!response?.code:
        if (response?.result?.common_result?.err_code) {
          response.result.common_result.code = response.code;
        }
        break;
      case !!response?.result?.common_result?.err_code:
        response.result.common_result.code = response.code;
        response.code = response.result.common_result.err_code;
        break;
      default:
        break;
    }
    return response;
  };

  /**
   * 统一处理响应 code：非 0 一律留在原页 toast，request 层不再跳异常页(与 next-h5 的 request 层一致)。
   * - 90001~90016 是 Kredit 网关层错误码(路由不存在/熔断/限流/未登录/签名无效等)，msg 是英文开发文案，
   *   统一弹本地化的 system_busy；90006(登录态失效)也按普通错误处理，用户退出重进即重新登录
   * - 登录与 get_user_info 两个接口传 manual，失败由 userInfo store 自己处理(未登录时回退走登录、登录后失败进异常页)
   * @param response
   * @param options
   */
  protected checkSpecCode = (response: any, options: any) => {
    const { url } = options;
    const code = response?.code;
    const isGatewayError = code >= 90001 && code <= 90016;
    switch (true) {
      case code !== 0 && options?.isUseDefaultErrorMsg:
        sendUserAction(UserActions.CommonProcesses.CallAPIFail, {
          url,
          code,
          request_id: response?.request_id || '',
        });
        // 如果是 seller app 且错误码是 92019（无权限），不显示 Toast
        if (!(code === NO_PERMISSION && isShopeeSellerApp())) {
          CreditToast.fail(formatMessage({ id: 'system_busy' }));
        }
        break;
      case code !== 0 && !options?.manual:
        sendUserAction(UserActions.CommonProcesses.CallAPIFail, {
          url,
          code,
          request_id: response?.request_id || '',
        });
        // 如果是 seller app 且错误码是 92019（无权限），不显示 Toast
        if (!(code === NO_PERMISSION && isShopeeSellerApp())) {
          // 网关码统一用本地化的系统繁忙；后端 msg 为空串时 ?? 兜不住，用 || 才会落到 network_error
          CreditToast.fail(
            isGatewayError
              ? formatMessage({ id: 'system_busy' })
              : response?.msg || formatMessage({ id: 'network_error' }),
          );
        }
        break;
      default:
        break;
    }
    if (code !== 0) {
      insight?.apiReport({
        url,
        resp_code: `${response?.code}`,
        message: `${response?.msg ?? 'code is not 0'}`,
      });
    }

    if (response?.code === 0 && response?.msg?.toLowerCase() !== 'success') {
      insight?.apiReport({
        url,
        resp_code: `${response?.code}`,
        message: response?.msg || 'unknown msg',
      });
    }
  };

  /**
   * 设置头部信息
   * @param signature
   * @param headers
   * @returns
   */
  protected setHeaders(headers: Record<string, any>) {
    const commonHeaders = {
      ...this.baseHeaders,
      'X-Kredit-Trace-ID': v4(),
      'X-Kredit-Start-Timestamp': `${Date.now()}`,
      'X-Kredit-System-Type': common.getDeviceSystem(),
      'X-Kredit-Rn-Version': `${this.rnVersion}`,
      'X-Kredit-Rn-Bundle-Version': `${this.shopeeRnBundleVersion}`,
      ...headers,
    };
    return commonHeaders;
  }

  /**
   * 请求错误的处理
   * @param e ResponseError
   * @returns void
   */
  private defaultErrorHandler(e: ResponseError) {
    const { response = {} as Response } = e || {};
    const options = (e?.request?.options ?? {}) as Record<string, any>;
    const headers = (options.headers as Record<string, string>) ?? {};
    const traceId = headers?.['X-Kredit-Trace-ID'] ?? '';
    const url = e?.request?.url ?? '';

    insight?.apiReport({
      url,
      resp_code: `-1`,
      message: 'api uncaught error',
    });

    // 超时、断网、HTTP 4xx/5xx 不经过 checkSpecCode，这里统一提示网络错误；
    // manual 的请求由调用方自己提示，503 已跳维护页不再提示
    if (!options.manual && response?.status !== ExceptionCode.MaintenanceException) {
      CreditToast.fail(formatMessage({ id: 'network_error' }));
    }

    return {
      // 避免response的status等于0的情况
      code: response?.status === 0 ? -1 : response?.status,
      traceId,
      msg: formatMessage({ id: 'network_error' }),
      errDeal: true,
      result: {},
    };
  }

  /**
   * 获取body header
   * @param neekRisk boolean
   * @returns Record<string, any>
   */
  private async getBodyHeader(neekRisk: boolean = false) {
    // 拿appInfo的信息
    const {
      deviceID,
      deviceFingerprint,
      from,
      model,
      brand,
      networkType,
      cellularType,
      deviceFingerprintToken,
    } = appInfo;
    const commonData = {
      version: this.version,
      platform_type: this.platForm, // 默认传1，1表示shopee
      // app_type: 1, // spm要求的传递请求参数：默认传1，1表示shopee
      // user_application_type: 3, // spm要求的传递请求参数：1表示PcWeb， 2表示mobile web，3表示mobile app；与spm dev确认，传3
      system_version: common.getSystemVersion(),
      lang: locale,
      from,
      app_version: `${this.appVersion}`,
      rn_version: `${this.rnVersion}`,
      shopee_rn_bundle_version: `${this.shopeeRnBundleVersion}`,
      region: this.region,
      system_type: common.getDeviceSystem(),
      device_id: deviceID,
      device_fingerprint: deviceFingerprint,
      device_fingerprint_token: deviceFingerprintToken,
    };
    // 重构后的新系统前端不直接调risk接口查询风控信息，而是去core查询，所以新系统不用传risk_device_info
    // let riskDeviceInfo;
    // try {
    //   // 第一次调用可能出现等待，后面调用直接返回结果
    //   const { data } = await getRiskInfo({
    //     metrics: {
    //       location: appInfo.gpsloc,
    //     },
    //   });
    //   riskDeviceInfo = data;
    // } catch (e) {
    //   riskDeviceInfo = '';
    // }

    let riskHeader: any = {};
    if (neekRisk) {
      riskHeader = {
        device_model: model,
        device_brand: brand,
        network_type: networkType,
        cellular_type: cellularType,
      };
    }
    return {
      ...commonData,
      ...riskHeader,
      // risk_device_info: riskDeviceInfo,
    };
  }

  /**
   * request
   * @param method 'POST' | 'PUT' | 'DELETE' | 'GET' = 'POST'
   * @param url {string} 请求url
   * @param data {Record<string, any>} 参数
   * @param toast {boolean} 是否默认弹错误提示
   * @param bodyHeader {Record<string, any>} 透传给网关的参数
   * @param headers {Record<string, any>} 请求头部的参数
   * @param options {Record<string, any>} umi-request options
   * @returns Promise
   */
  protected async request({
    method = 'POST',
    url,
    body = {},
    bodyHeader = {},
    headers = {},
    options = {},
    manual = false,
    isUseDefaultErrorMsg = false,
  }: IRequestParams) {
    try {
      const commonBodyHeader = await this.getBodyHeader();
      const requestData = {
        header: {
          ...commonBodyHeader,
          ...bodyHeader,
        },
        message: {
          biz_common: common.BizCommon,
          ...body,
        },
      };

      // 参数签名, fastescrow后端有自己的签名，挂在fast后端的接口不需要前端传递参数签名，只有直接调用UC的接口服务才需要
      // TODO:如果是非vn的region，需要传递appId和参数签名，防止uc接口需要校验,后续vn如果加上合同相关接口，也需要传递
      const signatureValue = signature(requestData, url);
      const appId = getEncryptConfig(url).appId;
      const combinedHeaders = {
        ...headers,
        'X-Kredit-Req-APPID': appId,
        'X-Kredit-Req-H': signatureValue,
      };

      // 重组Reqeust Header
      const umiHeaders = this.setHeaders(combinedHeaders);

      const resp = await this.umiRequest(url, {
        method,
        headers: umiHeaders,
        data: requestData,
        manual,
        isUseDefaultErrorMsg,
        ...options,
        credentials: 'include',
      });

      insight?.apiReport({
        url,
        resp_code: `${resp?.code}`,
        message: resp?.msg || 'unknown',
      });

      return resp;
    } catch (e) {
      insight?.apiReport({
        url,
        resp_code: `-1`,
        message: 'request error',
      });
      return {
        code: -1,
        msg: 'request error',
        result: {},
      };
    }
  }

  /**
   * POST 请求
   * @param url {string} 请求url
   * @param body {Record<string, any>} 参数
   * @param manual {boolean} 是否默认弹错误提示
   * @param isUseDefaultErrorMsg {boolean} 是否使用默认错误提示
   * @param bodyHeader {Record<string, any>} 透传给网关的参数
   * @param headers {Record<string, any>} 请求头部的参数
   * @param options {Record<string, any>} umi-request options
   * @returns Promise
   */
  async post<T = any>(
    url: string,
    params: Record<string, any> = {},
  ): Promise<{ code: number; result: T; msg: string }> {
    const pathname = window?.location?.pathname;

    // 更新risk的DeviceFingerprintToken
    const { needCheckDeviceFingerprintToken, token: deviceFingerprintToken } =
      await appInfo.setDeviceFingerprintToken(false, url);

    // 组装 bodyHeader
    const bodyHeader = {
      location: appInfo?.gpsloc,
      ...(params?.bodyHeader ?? {}),
      device_fingerprint_token: deviceFingerprintToken,
    };

    const options = {
      method: 'POST',
      url,
      ...params,
      bodyHeader,
    };

    const resp = await this.request(options as any);

    // @ts-ignore
    window?.__track_sdk?.setCache(pathname, url, resp);

    if (needCheckDeviceFingerprintToken && !deviceFingerprintToken) {
      insight?.apiReport({
        url,
        resp_code: `-1`,
        message: 'device fingerprint token error',
      });
    }

    return resp;
  }
}

export default new Request();
